import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { toISODate } from '../../../../../utils/date';

/**
 * AttendanceChart
 * Gráfico de linha SVG customizado que mostra a frequência.
 * Recebe os logs JÁ FILTRADOS e o range de datas ativo para gerar os pontos.
 */

const PRIMARY = "#1a2542";

const AttendanceChart = ({ logs = [], startDate, endDate }) => {
    const [hoveredIdx, setHoveredIdx] = useState(null);

    // Gera os dados do gráfico baseado no range real
    const chartData = useMemo(() => {
        const data = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Determina o range de datas para o eixo X
        let rangeStart, rangeEnd;

        if (startDate && endDate) {
            rangeStart = new Date(startDate + "T00:00:00");
            rangeEnd = new Date(endDate + "T00:00:00");
        } else {
            // ALL — usa o range dos logs, ou últimos 30 dias se não tiver logs
            if (logs.length > 0) {
                const dates = logs.map(l => l.date).filter(Boolean).sort();
                rangeStart = new Date(dates[0] + "T00:00:00");
                rangeEnd = new Date(dates[dates.length - 1] + "T00:00:00");
            } else {
                rangeEnd = today;
                rangeStart = new Date(today);
                rangeStart.setDate(rangeStart.getDate() - 30);
            }
        }

        // Se start == end (filtro "Hoje"), mostra 7 dias de contexto
        const diffDays = Math.ceil((rangeEnd - rangeStart) / (1000 * 60 * 60 * 24));
        if (diffDays < 2) {
            rangeStart = new Date(rangeEnd);
            rangeStart.setDate(rangeStart.getDate() - 6);
        }

        // Gera um ponto para cada dia no range
        const current = new Date(rangeStart);
        while (current <= rangeEnd) {
            const dateStr = toISODate(current);
            const weekdayShort = current.toLocaleDateString("pt-BR", { weekday: "short" }).replace(".", "");
            const dayMonth = `${String(current.getDate()).padStart(2, "0")}/${String(current.getMonth() + 1).padStart(2, "0")}`;

            let attendedCount = 0;
            if (logs.length > 0) {
                attendedCount = logs.filter(log =>
                    log.date === dateStr && log.status === 'ATTENDED'
                ).length;
            }

            data.push({
                fullDate: dateStr,
                displayDate: dayMonth,
                weekday: weekdayShort,
                count: attendedCount
            });

            current.setDate(current.getDate() + 1);
        }

        return data;
    }, [logs, startDate, endDate]);

    // Se não tem dados suficientes para um gráfico
    if (chartData.length < 2) {
        return (
            <div className="w-100 text-center py-4">
                <p className="text-muted font-size-13 mb-0">Dados insuficientes para gerar o gráfico.</p>
            </div>
        );
    }

    const maxVal = Math.max(...chartData.map(d => d.count), 1);

    // Calcula os pontos da curva
    const points = chartData.map((item, idx) => ({
        x: (idx / (chartData.length - 1)) * 100,
        y: 100 - (item.count / maxVal) * 85
    }));

    // Gera path smooth com Catmull-Rom splines
    const buildSmoothPath = (pts) => {
        if (pts.length < 2) return "";
        const tension = 0.3;

        let path = `M ${pts[0].x} ${pts[0].y}`;

        for (let i = 0; i < pts.length - 1; i++) {
            const p0 = pts[Math.max(0, i - 1)];
            const p1 = pts[i];
            const p2 = pts[i + 1];
            const p3 = pts[Math.min(pts.length - 1, i + 2)];

            const cp1x = p1.x + (p2.x - p0.x) * tension;
            const cp1y = p1.y + (p2.y - p0.y) * tension;
            const cp2x = p2.x - (p3.x - p1.x) * tension;
            const cp2y = p2.y - (p3.y - p1.y) * tension;

            path += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
        }

        return path;
    };

    const linePath = buildSmoothPath(points);
    const areaPath = linePath + ` L 100 100 L 0 100 Z`;

    // Labels: mostra todos se <= 10, senão espaça
    const labelInterval = chartData.length <= 10 ? 1 :
                          chartData.length <= 15 ? 2 :
                          chartData.length <= 31 ? 5 : 7;

    return (
        <div className="w-100">
            {/* Legenda */}
            <div className="d-flex justify-content-between align-items-center mb-2">
                <p className="text-muted font-size-13 fw-medium mb-0">Gráfico de Frequência</p>
                <span className="d-flex align-items-center gap-1 font-size-12 text-muted">
                    <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: PRIMARY, display: "inline-block" }}></span>
                    Presenças
                </span>
            </div>

            {/* Gráfico */}
            <div className="position-relative" style={{ height: "200px", paddingLeft: "30px", paddingRight: "10px" }}>

                {/* Eixo Y */}
                <div style={{ position: "absolute", left: 0, height: "100%", top: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", fontSize: "10px", color: "#9ca3af", fontWeight: "600", paddingBottom: "22px" }}>
                    <span style={{ width: "24px", textAlign: "right" }}>{maxVal}</span>
                    <span style={{ width: "24px", textAlign: "right" }}>{Math.round(maxVal / 2)}</span>
                    <span style={{ width: "24px", textAlign: "right" }}>0</span>
                </div>

                {/* Grid horizontal */}
                <div style={{ position: "absolute", left: "30px", right: "10px", height: "calc(100% - 22px)", top: 0, display: "flex", flexDirection: "column", justifyContent: "space-between", pointerEvents: "none" }}>
                    <div style={{ borderBottom: "1px solid #f3f4f6" }}></div>
                    <div style={{ borderBottom: "1px dashed #e5e7eb" }}></div>
                    <div style={{ borderBottom: "1px solid #f3f4f6" }}></div>
                </div>

                {/* SVG da linha */}
                <svg
                    style={{
                        position: "absolute",
                        top: 0,
                        left: "30px",
                        width: "calc(100% - 40px)",
                        height: "calc(100% - 22px)",
                        pointerEvents: "none"
                    }}
                    preserveAspectRatio="none"
                    viewBox="0 0 100 100"
                >
                    <defs>
                        <linearGradient id="areaGradientPrimary" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={PRIMARY} stopOpacity="0.18" />
                            <stop offset="100%" stopColor={PRIMARY} stopOpacity="0.02" />
                        </linearGradient>
                    </defs>

                    <path d={areaPath} fill="url(#areaGradientPrimary)" stroke="none" />

                    <path
                        d={linePath}
                        fill="none"
                        stroke={PRIMARY}
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        vectorEffect="non-scaling-stroke"
                    />
                </svg>

                {/* Zonas de hover + pontos + labels X */}
                <div
                    className="h-100 w-100 d-flex position-relative align-items-end justify-content-between"
                    style={{ paddingBottom: "22px" }}
                >
                    {chartData.map((item, idx) => {
                        const isHovered = hoveredIdx === idx;
                        const showLabel = idx % labelInterval === 0 || idx === chartData.length - 1;

                        return (
                            <div
                                key={idx}
                                className="h-100 d-flex flex-column align-items-center justify-content-end position-relative"
                                style={{ width: `${100 / chartData.length}%`, cursor: "pointer", zIndex: 10 }}
                                onMouseEnter={() => setHoveredIdx(idx)}
                                onMouseLeave={() => setHoveredIdx(null)}
                            >
                                {isHovered && (
                                    <div
                                        className="position-absolute bg-white shadow-sm rounded-0 p-2 text-center"
                                        style={{
                                            bottom: `${((item.count / maxVal) * 85) + 10}%`,
                                            width: "90px",
                                            left: "50%",
                                            transform: "translate(-50%, -8px)",
                                            border: "1px solid #e5e7eb",
                                            pointerEvents: "none",
                                            zIndex: 50
                                        }}
                                    >
                                        <div className="fw-bold font-size-11 text-muted mb-1">{item.displayDate} ({item.weekday})</div>
                                        <div className="fw-bold font-size-13" style={{ color: PRIMARY }}>{item.count} {item.count === 1 ? 'Aula' : 'Aulas'}</div>
                                    </div>
                                )}

                                {isHovered && (
                                    <div
                                        className="position-absolute"
                                        style={{
                                            bottom: `${(item.count / maxVal) * 85}%`,
                                            width: "8px",
                                            height: "8px",
                                            borderRadius: "50%",
                                            backgroundColor: "#fff",
                                            border: `2.5px solid ${PRIMARY}`,
                                            transform: "translate(0, 50%)",
                                            zIndex: 20,
                                            boxShadow: "0 1px 4px rgba(0,0,0,0.15)"
                                        }}
                                    ></div>
                                )}

                                {isHovered && (
                                    <div
                                        className="position-absolute"
                                        style={{
                                            bottom: 0,
                                            height: `${(item.count / maxVal) * 85}%`,
                                            width: "1px",
                                            backgroundColor: "rgba(26, 37, 66, 0.12)",
                                            pointerEvents: "none"
                                        }}
                                    ></div>
                                )}

                                <span
                                    className="position-absolute text-muted fw-medium"
                                    style={{
                                        bottom: "-20px",
                                        fontSize: "9px",
                                        opacity: showLabel ? 1 : 0,
                                        whiteSpace: "nowrap"
                                    }}
                                >
                                    {showLabel ? item.displayDate : ''}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

AttendanceChart.propTypes = {
    logs: PropTypes.array,
    startDate: PropTypes.string,
    endDate: PropTypes.string
};

export default AttendanceChart;
